// src/services/reviewService.js - SAFE VERSION
// Preserves ALL original functionality + adds fake reviews display
import { db, auth } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment
} from 'firebase/firestore';

// Import fake reviews data
import fakeReviews, { getReviewStats as getFakeStats } from '../components/reviewsData';

// Add helpful counts to fake reviews for UI
const fakeReviewsWithHelpful = fakeReviews.map(review => ({
  ...review,
  helpful: Math.floor(Math.random() * 50),
  createdAt: new Date(review.date),
  isFake: true // Mark as fake so we don't try to update them
}));

class ReviewService {
  constructor() {
    this.collectionName = 'reviews';
  }

  /**
   * Submit a new review - STARTS IN PENDING STATUS
   * ⚠️ ORIGINAL CODE - UNCHANGED
   */
  async submitReview(reviewData) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to submit a review');
    }

    try {
      // Check if user already submitted a review
      const existingReview = await this.getUserReview(user.uid);
      if (existingReview) {
        throw new Error('Sie haben bereits eine Bewertung abgegeben');
      }

      // Get user profile for display name
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.exists() ? userDoc.data() : {};

      const review = {
        userId: user.uid,
        userEmail: user.email,
        userName: userData.firstName || user.displayName || 'Anonymer Nutzer',
        userInitials: this.generateInitials(userData.firstName, userData.lastName, user.displayName),
        rating: reviewData.rating,
        title: reviewData.title || '',
        comment: reviewData.comment,
        plan: reviewData.plan || null,
        verified: user.emailVerified,
        helpful: 0,
        reportCount: 0,
        status: 'pending', // All reviews start as pending
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.collectionName), review);

      // Update review statistics
      await this.updateReviewStats();

      return { success: true, reviewId: docRef.id };
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  /**
   * Get all reviews - MODIFIED to include fake reviews
   * Shows: Fake reviews (30) + Real published reviews (0+)
   */
  async getReviews(options = {}) {
    try {
      const {
        limitCount = 20,
        sortBy = 'newest',
        filterRating = null,
        filterPlan = null
      } = options;

      // START WITH FAKE REVIEWS
      let allReviews = [...fakeReviewsWithHelpful];

      // GET REAL PUBLISHED REVIEWS FROM FIRESTORE (YOUR ORIGINAL CODE)
      try {
        let reviewQuery = query(
          collection(db, this.collectionName),
          where('status', '==', 'published')
        );

        // Apply filters (YOUR ORIGINAL CODE)
        if (filterRating) {
          reviewQuery = query(reviewQuery, where('rating', '==', filterRating));
        }
        if (filterPlan) {
          reviewQuery = query(reviewQuery, where('plan', '==', filterPlan));
        }

        const querySnapshot = await getDocs(reviewQuery);

        // Add real reviews to the array
        querySnapshot.forEach((docSnapshot) => {
          allReviews.push({
            id: docSnapshot.id,
            ...docSnapshot.data(),
            createdAt: docSnapshot.data().createdAt?.toDate(),
            updatedAt: docSnapshot.data().updatedAt?.toDate(),
            isFake: false // Mark as real
          });
        });
      } catch (firestoreError) {
        console.log('No real reviews yet or Firestore error');
      }

      // Filter by rating if specified
      if (filterRating) {
        allReviews = allReviews.filter(r => r.rating === filterRating);
      }

      // Apply sorting (YOUR ORIGINAL LOGIC)
      switch (sortBy) {
        case 'newest':
          allReviews.sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
          break;
        case 'oldest':
          allReviews.sort((a, b) => new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date));
          break;
        case 'highest':
          allReviews.sort((a, b) => b.rating - a.rating);
          break;
        case 'lowest':
          allReviews.sort((a, b) => a.rating - b.rating);
          break;
        case 'helpful':
          allReviews.sort((a, b) => (b.helpful || 0) - (a.helpful || 0));
          break;
      }

      return allReviews.slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Fallback to just fake reviews if everything fails
      return fakeReviewsWithHelpful.slice(0, limitCount);
    }
  }

  /**
   * Get review statistics - MODIFIED to include fake reviews
   * Shows: Combined stats from fake + real reviews
   */
  async getReviewStats() {
    try {
      // Get fake stats
      const fakeStats = getFakeStats();

      // Try to get real stats (YOUR ORIGINAL CODE)
      try {
        const reviewsRef = collection(db, this.collectionName);
        const publishedQuery = query(reviewsRef, where('status', '==', 'published'));
        const querySnapshot = await getDocs(publishedQuery);

        let realCount = 0;
        let realTotalRating = 0;
        const realDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

        querySnapshot.forEach((doc) => {
          const review = doc.data();
          realCount++;
          realTotalRating += review.rating;
          realDist[review.rating]++;
        });

        // Combine fake + real
        const combinedTotal = fakeStats.totalReviews + realCount;
        const combinedRatingSum = (fakeStats.totalReviews * fakeStats.averageRating) + realTotalRating;
        const combinedAverage = combinedTotal > 0 ? (combinedRatingSum / combinedTotal).toFixed(1) : '0.0';

        return {
          totalReviews: combinedTotal,
          averageRating: parseFloat(combinedAverage),
          ratingDistribution: {
            1: fakeStats.ratingDistribution[1] + realDist[1],
            2: fakeStats.ratingDistribution[2] + realDist[2],
            3: fakeStats.ratingDistribution[3] + realDist[3],
            4: fakeStats.ratingDistribution[4] + realDist[4],
            5: fakeStats.ratingDistribution[5] + realDist[5]
          }
        };
      } catch (firestoreError) {
        // If no real reviews, just return fake stats
        return fakeStats;
      }
    } catch (error) {
      console.error('Error fetching review stats:', error);
      return getFakeStats();
    }
  }

  /**
   * Check if current user has already submitted a review
   * ⚠️ ORIGINAL CODE - UNCHANGED
   */
  async getUserReview(userId = null) {
    try {
      const currentUserId = userId || auth.currentUser?.uid;
      if (!currentUserId) return null;

      const reviewQuery = query(
        collection(db, this.collectionName),
        where('userId', '==', currentUserId)
      );

      const querySnapshot = await getDocs(reviewQuery);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        };
      }

      return null;
    } catch (error) {
      console.error('Error checking user review:', error);
      return null;
    }
  }

  /**
   * Mark review as helpful
   * ⚠️ MODIFIED - Prevents updating fake reviews
   */
  async markHelpful(reviewId) {
    try {
      // Check if it's a fake review (ID 1-30 or has isFake flag)
      if (typeof reviewId === 'number' && reviewId <= 30) {
        console.log('Cannot mark fake review as helpful');
        return true; // Pretend success for smooth UX
      }

      const reviewRef = doc(db, this.collectionName, reviewId);
      await updateDoc(reviewRef, {
        helpful: increment(1)
      });
      return true;
    } catch (error) {
      console.error('Error marking review as helpful:', error);
      return false;
    }
  }

  /**
   * Report a review
   * ⚠️ MODIFIED - Prevents reporting fake reviews
   */
  async reportReview(reviewId, reason) {
    try {
      // Check if it's a fake review
      if (typeof reviewId === 'number' && reviewId <= 30) {
        console.log('Cannot report fake review');
        return true; // Pretend success for smooth UX
      }

      const reviewRef = doc(db, this.collectionName, reviewId);
      await updateDoc(reviewRef, {
        reportCount: increment(1),
        lastReportReason: reason,
        lastReportedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error reporting review:', error);
      return false;
    }
  }

  /**
   * Update overall review statistics (internal method)
   * ⚠️ ORIGINAL CODE - UNCHANGED
   */
  async updateReviewStats() {
    try {
      const stats = await this.getReviewStats();
      const statsRef = doc(db, 'siteStats', 'reviews');

      await updateDoc(statsRef, {
        ...stats,
        lastUpdated: serverTimestamp()
      }).catch(async () => {
        await setDoc(statsRef, {
          ...stats,
          lastUpdated: serverTimestamp()
        });
      });
    } catch (error) {
      console.error('Error updating review stats:', error);
    }
  }

  /**
   * Generate user initials for display
   * ⚠️ ORIGINAL CODE - UNCHANGED
   */
  generateInitials(firstName, lastName, displayName) {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (displayName) {
      const names = displayName.split(' ');
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase();
      }
      return names[0].charAt(0).toUpperCase();
    }
    return 'NU';
  }

  /**
   * Format relative time for review display
   * ⚠️ ORIGINAL CODE - UNCHANGED
   */
  formatRelativeTime(date) {
    if (!date) return 'Kürzlich';

    const now = new Date();
    const reviewDate = date instanceof Date ? date : new Date(date);
    const diffInSeconds = Math.floor((now - reviewDate) / 1000);

    if (diffInSeconds < 60) return 'Gerade eben';
    if (diffInSeconds < 3600) return `vor ${Math.floor(diffInSeconds / 60)} Minuten`;
    if (diffInSeconds < 86400) return `vor ${Math.floor(diffInSeconds / 3600)} Stunden`;
    if (diffInSeconds < 2592000) return `vor ${Math.floor(diffInSeconds / 86400)} Tagen`;
    if (diffInSeconds < 31536000) return `vor ${Math.floor(diffInSeconds / 2592000)} Monaten`;
    return `vor ${Math.floor(diffInSeconds / 31536000)} Jahren`;
  }
}

export default new ReviewService();